import axios from 'axios';
import { createContext, useCallback, useContext } from 'react';
import {
  AssistantFrameBulkDeleteApi,
  AssistantFrameCreateApi,
  AssistantFrameExportApi,
  AssistantFrameExtendedApi,
  AssistantFrameFilters,
  AssistantFrameListApi,
  AssistantFrameUpdateApi,
} from './AgentFrameApiProviderTypes';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import { buildUrl } from '../utils/buildQuery';

interface PromptApiContextType {
  getAgentFrames: (
    skip?: number,
    limit?: number,
    filters?: AssistantFrameFilters | null,
  ) => Promise<AssistantFrameListApi>;
  getAgentFrameById: (agentFrameId: string) => Promise<AssistantFrameExtendedApi>;
  createAgentFrame: (data: AssistantFrameCreateApi) => Promise<AssistantFrameExtendedApi>;
  updateAgentFrame: (
    agentFrameId: string,
    data: AssistantFrameUpdateApi,
  ) => Promise<AssistantFrameExtendedApi>;
  deleteAgentFrame: (agentFrameId: string) => Promise<void>;
  bulkDeleteAgentFrames: (data: AssistantFrameBulkDeleteApi) => Promise<void>;
  importAgentFrames: (file: File) => Promise<AssistantFrameListApi>;
  exportAgentFrames: (params: AssistantFrameExportApi) => Promise<Blob>;
}

const AgentFrameApiContext = createContext<PromptApiContextType | null>(null);

export function AgentFrameApiProvider({ children }) {
  const { get, put, post, del, postUpload } = useApiService(CONFIG.SERVER_ASSISTANT);

  const getAgentFrames = useCallback(
    async (
      skip: number = 0,
      limit: number = 10,
      filters: AssistantFrameFilters | null = null,
    ): Promise<AssistantFrameListApi> => {
      try {
        const trim = (v?: string | null) => {
          const t = v?.trim();
          return t ? t : null;
        };
        const response = await get(
          buildUrl('/v1/assistant_frames', {
            skip,
            limit,
            name: trim(filters?.name),
            instruction: trim(filters?.instruction),
            topic: trim(filters?.topic),
            criteria: trim(filters?.criteria),
            thematics:
              filters?.thematics && filters.thematics.length > 0 ? filters.thematics : null,
          }),
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching agent frames:', error);
        throw error;
      }
    },
    [get],
  );

  const getAgentFrameById = useCallback(
    async (agentFrameId: string): Promise<AssistantFrameExtendedApi> => {
      try {
        const response = await get(`/v1/assistant_frames/${agentFrameId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching agent frame by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createAgentFrame = useCallback(
    async (data: AssistantFrameCreateApi): Promise<AssistantFrameExtendedApi> => {
      try {
        const response = await put(`/v1/assistant_frames`, data);
        return response.data;
      } catch (error) {
        console.error('Error creating agent frame:', error);
        throw error;
      }
    },
    [put],
  );

  const updateAgentFrame = useCallback(
    async (
      agentFrameId: string,
      data: AssistantFrameUpdateApi,
    ): Promise<AssistantFrameExtendedApi> => {
      try {
        const response = await put(`/v1/assistant_frames/${agentFrameId}`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating agent frame:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteAgentFrame = useCallback(
    async (agentFrameId: string): Promise<void> => {
      try {
        const response = await del(`/v1/assistant_frames/${agentFrameId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting agent frame:', error);
        throw error;
      }
    },
    [del],
  );

  const bulkDeleteAgentFrames = useCallback(
    async (data: AssistantFrameBulkDeleteApi): Promise<void> => {
      try {
        const response = await post(`/v1/assistant_frames/bulk-delete`, data);
        return response.data;
      } catch (error) {
        console.error('Error bulk deleting agent frames:', error);
        throw error;
      }
    },
    [post],
  );

  const importAgentFrames = useCallback(
    async (file: File): Promise<AssistantFrameListApi> => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await postUpload(`/v1/assistant_frames/import`, formData);
        return response.data;
      } catch (error) {
        const serverMessage = axios.isAxiosError(error)
          ? ((error.response?.data as { error?: string; message?: string } | undefined)?.error ??
            (error.response?.data as { error?: string; message?: string } | undefined)?.message)
          : undefined;
        console.error('Error importing agent frames:', serverMessage ?? error);
        throw error;
      }
    },
    [postUpload],
  );

  const exportAgentFrames = useCallback(
    async (params: AssistantFrameExportApi): Promise<Blob> => {
      try {
        const isKeysExport = 'keys' in params;

        const body = isKeysExport
          ? { keys: params.keys }
          : {
              name: params.name,
              instruction: params.instruction,
              topic: params.topic,
              criteria: params.criteria,
              thematics: params.thematics,
            };

        const response = await post('/v1/assistant_frames/export', body, {
          responseType: 'blob',
        });

        return response.data;
      } catch (error) {
        console.error('Error exporting agent frames:', error);
        throw error;
      }
    },
    [post],
  );

  return (
    <AgentFrameApiContext.Provider
      value={{
        getAgentFrames,
        getAgentFrameById,
        createAgentFrame,
        updateAgentFrame,
        deleteAgentFrame,
        bulkDeleteAgentFrames,
        importAgentFrames,
        exportAgentFrames,
      }}
    >
      {children}
    </AgentFrameApiContext.Provider>
  );
}

export function useAgentFrameApi(): PromptApiContextType {
  const context = useContext(AgentFrameApiContext);
  if (!context) {
    throw new Error('useAgentFrameApi must be used within an AgentFrameApiProvider');
  }
  return context;
}
